import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_Link, ValidateHeader } from '../../common/Constants.js';
import { triggerBackgroundApis } from '../../services/backgroundApiService.js'; // Import the service
import Swal from 'sweetalert2';

const initialState = {
  value: null,
  user: { name: '', email: '', password: '' },
  status: 'idle',
  error: null,
};

// Async thunks for API requests
export const login = createAsyncThunk(
  'user/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_Link}login`,
        {
          email,
          password,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'plims-auth': ValidateHeader,
          },
        }
      );

      // Extract token from response
      const token = response.data.token || response.data.data?.token;

      // Trigger background APIs - this does NOT wait
      if (token) {
        triggerBackgroundApis(token);
      }

      // Return immediately - login is instant
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const signup = createAsyncThunk(
  'user/signup',
  async ({ name, email, password }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_Link}register`,
        {
          name,
          email,
          password,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'plims-auth': ValidateHeader,
          },
        }
      );

      if (response.data.message === 'User Registered successfully.') {
        Swal.fire({
          title: 'Your Account is Created successfully',
          text: 'Your account requires administrative approval. Your email address will get a confirmation email.',
          icon: 'success',
          confirmButtonText: 'Okay',
        }).then(function () {
          window.location.href = '/';
        });
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const logout = createAsyncThunk('user/logout', async (token) => {
  try {
    const response = await axios.post(`${API_Link}logout`, null, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'plims-auth': ValidateHeader,
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.message === 'Unauthenticated.') {
      sessionStorage.clear();
      localStorage.clear();
    }
    throw error;
  }
});

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.value = true;
        state.user = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(signup.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.value = true;
        state.user = action.payload;
      })
      .addCase(signup.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(logout.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.status = 'succeeded';
        state.value = false;
        state.user = { name: '', email: '', password: '' };
      })
      .addCase(logout.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

export const userReducer = userSlice.reducer;
export default userSlice.reducer;